// The DFT module (Quantum ESPRESSO runs; running is Advanced plan): the server's routes
// (server/prayog_server/api/dft.py), helpers that read and change a recipe (docs/QE_RECIPES.md), and the way from a
// finished run into SKFitter ("Import from a DFT project", "Use in SKFitter").
import { useQuery } from "@tanstack/react-query";
import { del, get, post, put, type Project } from "./api";
import type { StructureView } from "./lattice";

export type Recipe = Record<string, any>;

export interface QeTemplate { id: string; name: string; description: string; file: string }

export interface QeStatus {
  available: boolean;              // a QE worker is deployed on this server
  allowed: boolean;                // the user's plan includes Quantum ESPRESSO runs
  limits: Record<string, { value: number | boolean; label: string; unit: string }>;
  templates: QeTemplate[];
  library: { files: number; elements: string[]; sources: string[] };
  libraries?: QeLibrary[];         // the pseudopotential libraries a recipe can take its files from
  protocols?: { id: ProtocolId; label: string; about: string; library: string | null }[];
  nprocs: number;
  budget?: QeBudget;               // the user's Quantum ESPRESSO hours this month (UTC)
  queue: { running: number; queued: number };
  my_jobs: { id: string; status: string; position: number | null; project_id: string | null }[];
}

/** dft.qe_hours_month: hours of the plan (or the admin's override), used and left this month. */
export interface QeBudget { hours: number; used: number; left: number }

/** Hours as "3 h 05 min" / "40 min". */
export function fmtHours(h: number): string {
  const m = Math.round(h * 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min` : `${m} min`;
}

export type ProtocolId = "fast" | "balanced" | "precise";
/** A pseudopotential library of the server (a source table: SSSP, PseudoDojo, ...). */
export interface QeLibrary {
  name: string; description: string; family: string; functional: string; relativistic: string; accuracy: string;
  url: string; licence: string; citation: string; elements: number; files: number; levels: string[];
}
/** A recipe's protocol (how it was made): fast / balanced / precise with a library. */
export interface RecipeProtocol { name: ProtocolId | null; library: string | null }
export const recipeProtocol = (r: Recipe | null | undefined): RecipeProtocol | null =>
  r?.protocol ? { name: r.protocol.name ?? null, library: r.protocol.library ?? null } : null;

/** A UPF file uploaded to a DFT project: what it is, or why it cannot be used. */
export interface UserPseudo {
  file_id: string; name: string; size: number; problem: string | null; element?: string; pseudo_type?: string;
  relativistic?: string; functional?: string; z_valence?: number | null; orbitals?: string; ecutwfc?: number | null;
}
export const USER_PSEUDO_MAX_BYTES = 10 * 1024 * 1024;

export interface PseudoOption {
  file: string; element: string; source: string; ecutwfc: number; ecutrho: number; relativistic: string;
  pseudo_type: string; functional: string; z_valence: number | null; orbitals: string; projections: boolean;
  recommended: boolean; notes: string[];
}

export interface PseudoTable {
  spin: string;
  options: Record<string, PseudoOption[]>;
  missing: string[];
  recommended_cutoffs: { ecutwfc: number; ecutrho: number };
}

export interface QeIssue { level: "error" | "warning" | "note"; code: string; where: string; message: string }

export interface QeEstimate {
  atoms: number; volume_A3: number | null; plane_waves: number | null; fft_grid: number[] | null;
  kpoints_scf: number | null; kpoints_bands: number; bands: number | null; spinor: number; spins: number;
  memory_mb: number | null; work: number | null;
  fft_smooth?: number[] | null; kpoints_scf_irr?: number | null; bands_scf?: number | null;
  serial_seconds?: number | null;                // the run-time model's time on one process (skfitter_qe.estimate_size)
}

export interface QeCheck {
  ok: boolean; allowed: boolean; issues: QeIssue[]; estimate: QeEstimate | null; expected_outputs: string[] | null;
  inputs: Record<string, string> | null; yaml: string;
}

/** What a dft.qe job adds to the DFT result. */
export interface QeSummary {
  qe_version: string | null; fermi_energy_eV: number | null; fermi_source: string | null; gap_eV: number | null;
  kpoints_path: number; bands: number; electrons: number; lsda: boolean; noncolin: boolean; spinorbit: boolean;
  seconds: number; steps: Record<string, { seconds: number; iterations?: number; bfgs_steps?: number }>; warnings: string[]; nprocs: number;
  recipe: string | null; template: string | null; pdos_files?: number;
  gap_direct?: boolean | null; total_magnetization?: number | null; absolute_magnetization?: number | null; site_moments?: number[] | null;
  dos?: { points: number; emin_eV: number; emax_eV: number; spin: boolean; projections: string[]; kmesh: number[]; degauss_Ry: number } | null;
  relax?: RelaxSummary | null;
}

/** The relaxation of a run (recipe field relax; pw.x 'relax' before the SCF). */
export interface RelaxSummary {
  converged: boolean; bfgs_steps: number; scf_cycles: number; energy_start_eV: number | null; energy_final_eV: number | null;
  energy_change_eV: number; total_force_Ry_bohr: number | null; forc_conv_thr: number | null; atoms: number; fixed_atoms: number;
  max_displacement_A: number; displacements: { atom: number; species: string; d_A: number }[]; all_displacements_A?: number[];
}

/** A run's density of states (GET /dft/runs/{job}/dos): eV, states / eV / cell. */
export interface QeDos {
  energies: number[]; spin: boolean; fermi_eV: number | null; fermi_nscf_eV?: number | null; kmesh?: number[]; degauss_Ry?: number;
  total: { dos?: number[]; up?: number[]; down?: number[] };
  projected: { element: string; species: string[]; l: string; dos?: number[]; up?: number[]; down?: number[] }[];
  /** a defect supercell's DOS on its atoms and neighbour shells (D-2) */
  groups?: { name: string; atoms: number[]; dos?: number[]; up?: number[]; down?: number[] }[];
}
/** One run of a DFT project (GET /dft/projects/{id}/runs). */
export interface QeRun {
  job_id: string; status: string; seed: string | null; name: string | null; template: string | null; created_at: string | null;
  wall_seconds: number | null; error: string; settings: Record<string, string | null | undefined>;
  gap_eV: number | null; fermi_energy_eV: number | null; seconds: number | null; has_dos: boolean; has_bands: boolean;
  provenance_file: string | null; zip_file: string | null;
}
/** POST /dft/compare: the runs' bands (E - E_F, one x axis) and the settings, one row each. */
export interface QeCompare {
  runs: { job_id: string; seed: string | null; name: string | null; fermi_eV: number; x: number[]; ticks: [number, string][];
    lsda: boolean; bands?: number[][]; up?: number[][]; down?: number[][] }[];
  rows: { key: string; label: string; values: string[]; differs: boolean; result: boolean }[];
  same_path: boolean; note: string;
}

export type CellChoice = "primitive" | "conventional" | "as_given";
export interface StructureOptions { cell: CellChoice; scale: number; vacuum: number | null }

/** What the server says about a structure file it read into a recipe (POST /dft/structure/read). */
export interface StructureInfo {
  format: string; filename: string; formula: string; spacegroup: { symbol: string; number: number }; cell: CellChoice;
  atoms: { file: number; primitive: number; conventional: number; used: number };
  abc: number[]; angles: number[]; vacuum_axes: number[]; vacuum_gap: number | null; dimensionality: string;
  path_labels: string[]; kmesh: number[]; missing_pseudos: string[]; warnings: string[]; notes: string[];
  protocol?: RecipeProtocol | null;
}

/** A DFT project's memory (project.settings.dft): the recipe being prepared or last run, the seedname, the last job,
 * and the structure file it was made from (kept when small, so the cell can be chosen again). */
export interface DftMemory {
  seed: string;
  qe?: { recipe: Record<string, any>; template?: string | null; changed?: boolean };
  job?: string;
  structure_file?: { name: string; text: string | null; options: StructureOptions; info: StructureInfo; source?: DbSource };
}

// == structure databases (COD, OPTIMADE providers, the Materials Project) =============================================
export type DbId = "cod" | "optimade" | "mp";
export type DbKind = "formula" | "elements" | "id" | "text";
/** Where a structure came from (an entry of a database). */
export interface DbSource { database: DbId; provider?: string; id: string; url: string; label: string }
export interface DbInfo {
  databases: { id: DbId; name: string; available: boolean; enabled?: boolean; providers?: string[]; key?: "site" | "project" | null;
    about: string }[];
  project_key: { stored: boolean; hint?: string; readable?: boolean; updated_at?: string | null };
}
export interface DbRow {
  id: string; formula: string | null; name: string; spacegroup: string | null; abc: (number | null)[] | null;
  angles: (number | null)[] | null; nsites: number | null; detail: string; temperature_K?: number | null;
  usable: boolean; problem: string | null; url: string | null;
}
export interface DbQuery { database: DbId; provider?: string | null; project_id?: string; kind: DbKind; value: string; only: boolean }
export const dbList = (projectId: string) => get<DbInfo>(`/dft/databases?project_id=${encodeURIComponent(projectId)}`);
export const dbSearch = (q: DbQuery) => post<{ total: number; results: DbRow[]; more?: boolean }>("/dft/databases/search", q);
export const dbFetch = (q: { database: DbId; provider?: string | null; project_id?: string; id: string }) =>
  post<{ filename: string; text: string; source: DbSource }>("/dft/databases/fetch", q);
export const setProjectMpKey = (projectId: string, key: string) => put<DbInfo["project_key"]>(`/dft/projects/${projectId}/mp_key`, { key });
export const removeProjectMpKey = (projectId: string) => del<{ removed: boolean }>(`/dft/projects/${projectId}/mp_key`);
export const STRUCTURE_TEXT_KEPT = 200_000;   // characters of a structure file kept in the project's memory

/** A finished DFT run, as SKFitter's "Import from a DFT project" lists it (GET /dft/runs). */
export interface DftRun {
  project_id: string; project_name: string; job_id: string; code: string; seed: string | null; recipe: string | null;
  template: string | null; formula: string | null; spacegroup: string | null; gap_eV: number | null;
  fermi_energy_eV: number | null; kpoints: number | null; bands: number | null; soc: boolean | null;
  magnetic: boolean | null; has_projection: boolean | null; roles: string[]; created_at: string | null;
}

export const SPIN_MODES = [
  { value: "none", label: "No spin" }, { value: "collinear", label: "Collinear (magnetic)" },
  { value: "noncollinear", label: "Non-collinear" }, { value: "soc", label: "Spin-orbit coupling" },
];
export const SMEARINGS = [
  { value: "mv", label: "Marzari-Vanderbilt (cold)" }, { value: "gaussian", label: "Gaussian" },
  { value: "mp", label: "Methfessel-Paxton" }, { value: "fd", label: "Fermi-Dirac" },
];

export function useQeStatus() {
  return useQuery({ queryKey: ["qe-status"], queryFn: () => get<QeStatus>("/dft"), staleTime: 30_000 });
}
export const qeTemplate = (id: string) => get<{ id: string; recipe: Recipe; yaml: string }>(`/dft/templates/${id}`);
export const qeCheck = (recipe: Recipe, projectId?: string) =>
  post<QeCheck>("/dft/check", projectId ? { recipe, project_id: projectId } : { recipe });
export const qeProtocol = (recipe: Recipe, protocol: ProtocolId, library: string | null) =>
  post<{ recipe: Recipe; yaml: string; notes: string[] }>("/dft/protocol", { recipe, protocol, library });
export const projectPseudos = (projectId: string) => get<UserPseudo[]>(`/dft/projects/${projectId}/pseudos`);
export const qeParse = (text: string, fmt?: "yaml" | "json") =>
  post<{ recipe: Recipe; yaml: string }>("/dft/parse", { text, fmt: fmt ?? null });
export const qeImport = (scf: string, bands: string | null, name = "") =>
  post<{ recipe: Recipe; yaml: string; issues: QeIssue[] }>("/dft/import", { scf, bands, name });
export const qePseudos = (elements: string[], spin: string, library?: string | null, projectId?: string) =>
  get<PseudoTable>(`/dft/pseudos?elements=${encodeURIComponent(elements.join(","))}&spin=${encodeURIComponent(spin)}`
    + (library ? `&library=${encodeURIComponent(library)}` : "") + (projectId ? `&project_id=${encodeURIComponent(projectId)}` : ""));

// -- reading a recipe -------------------------------------------------------------------------------------------
export interface SpeciesRow { label: string; element: string; pseudo: string; count: number }

/** The element symbol of a species label ("Fe1" -> "Fe", "Mn_" -> "Mn"), as skfitter_qe.element_of. */
export const structureRead = (text: string, filename: string, o: StructureOptions, spin = "none", source = "",
  protocol: RecipeProtocol | null = null) =>
  post<{ recipe: Recipe; yaml: string; info: StructureInfo }>("/dft/structure/read",
    { text, filename, cell: o.cell, scale: o.scale, vacuum: o.vacuum, spin, source,
      ...(protocol?.name ? { protocol: protocol.name, library: protocol.library } : {}) });
export const recipeStructureView = (recipe: Recipe) => post<StructureView>("/dft/structure/view", { recipe });
export const dftRuns = () => get<DftRun[]>("/dft/runs");
/** Copies of a run's files in an SKFitter project, as uploaded Quantum ESPRESSO files. */
export const importDftRun = (projectId: string, jobId: string) =>
  post<{ code: string; files: Record<string, string>; seed: string }>("/skfitter/import_dft", { project_id: projectId, job_id: jobId });
/** "Use in SKFitter": a new SKFitter project with copies of the run's files, set up to read them. */
export const runToSkfitter = (jobId: string) => post<Project>(`/dft/runs/${jobId}/to_skfitter`, {});
export const qeRuns = (projectId: string) => get<QeRun[]>(`/dft/projects/${projectId}/runs`);
export const qeDos = (jobId: string) => get<QeDos>(`/dft/runs/${jobId}/dos`);
export const qeCompare = (jobIds: string[], emin: number, emax: number) =>
  post<QeCompare>("/dft/compare", { job_ids: jobIds, emin, emax });

/** The recipe's density of states: on (with the default k-spacing) or off. */
export function withDos(r: Recipe, on: boolean, kspacing?: number): Recipe {
  const { dos: _old, ...rest } = r;
  return on ? { ...rest, dos: { ...(typeof _old === "object" && _old ? _old : {}), ...(kspacing ? { kspacing } : {}) } } : rest;
}

/** The DOS curves to draw (E - E_F): the total and one per element (all orbitals) or per element and orbital;
 * spin-polarised runs give up (positive) and down (negative). */
export function dosCurves(d: QeDos, by: "element" | "orbital" | "defect"): { name: string; y: number[]; spin: "up" | "down" | null }[] {
  const out: { name: string; y: number[]; spin: "up" | "down" | null }[] = [];
  const add = (name: string, src: { dos?: number[]; up?: number[]; down?: number[] }) => {
    if (d.spin) {
      out.push({ name: `${name} ↑`, y: src.up ?? [], spin: "up" });
      out.push({ name: `${name} ↓`, y: (src.down ?? []).map((v) => -v), spin: "down" });
    } else out.push({ name, y: src.dos ?? [], spin: null });
  };
  add("Total", d.total);
  if (by === "defect") (d.groups ?? []).forEach((g) => add(`${g.name[0].toUpperCase()}${g.name.slice(1)} (${g.atoms.length} atom${g.atoms.length > 1 ? "s" : ""})`, g));
  else if (by === "orbital") d.projected.forEach((p) => add(`${p.element} ${p.l}`, p));
  else {
    const els = [...new Set(d.projected.map((p) => p.element))];
    for (const el of els) {
      const parts = d.projected.filter((p) => p.element === el);
      const sum = (k: "dos" | "up" | "down") => d.energies.map((_, i) => parts.reduce((a, p) => a + (p[k]?.[i] ?? 0), 0));
      add(el, d.spin ? { up: sum("up"), down: sum("down") } : { dos: sum("dos") });
    }
  }
  return out;
}

export function elementOf(label: string): string {
  const m = /^([A-Z][a-z]?)/.exec(label ?? "");
  return m ? m[1] : label;
}

/** The species of a recipe (mapping or list form), with how many atoms each has. */
export function speciesRows(r: Recipe): SpeciesRow[] {
  const sp = r?.species;
  const list: { label: string; pseudo?: string }[] = Array.isArray(sp)
    ? sp.map((s: any) => (typeof s === "object" ? { label: String(s.label), pseudo: s.pseudo } : { label: String(s) }))
    : Object.entries(sp ?? {}).map(([label, v]: [string, any]) => ({ label, pseudo: typeof v === "string" ? v : v?.pseudo }));
  const atoms: any[] = r?.structure?.atoms ?? [];
  return list.map((s) => ({
    label: s.label, element: elementOf(s.label), pseudo: s.pseudo ?? "",
    count: atoms.filter((a) => (Array.isArray(a) ? a[0] : a?.species) === s.label).length,
  }));
}

export function uniqueElements(rows: SpeciesRow[]): string[] {
  return [...new Set(rows.map((r) => r.element))];
}

/** A copy of the recipe with one value changed (path of keys; missing objects are made). undefined removes it. */
export function setIn(r: Recipe, path: (string | number)[], value: unknown): Recipe {
  const out = structuredClone(r ?? {});
  let o: any = out;
  for (const k of path.slice(0, -1)) {
    if (o[k] == null || typeof o[k] !== "object") o[k] = {};
    o = o[k];
  }
  const last = path[path.length - 1];
  if (value === undefined) delete o[last];
  else o[last] = value;
  return out;
}

export function getIn(r: Recipe, path: (string | number)[]): any {
  return path.reduce((o: any, k) => (o == null ? undefined : o[k]), r);
}

/** The recipe with species `label` using pseudopotential `file` (either form of the species list). */
export function setPseudo(r: Recipe, label: string, file: string): Recipe {
  if (Array.isArray(r.species)) {
    const out = structuredClone(r);
    out.species = out.species.map((s: any) => (s?.label === label ? { ...s, pseudo: file } : s));
    return out;
  }
  const cur = r.species?.[label];
  return setIn(r, ["species", label], typeof cur === "object" && cur ? { ...cur, pseudo: file } : { pseudo: file });
}

/** Cutoffs for a choice of files: the largest recommended values; ecutrho at least 8 x ecutwfc with PAW /
 * ultrasoft files, 4 x otherwise, in whole Rydbergs (as skfitter_qe_library.cutoffs_for). null when nothing is known. */
export function cutoffsFor(chosen: PseudoOption[]): { ecutwfc: number; ecutrho: number } | null {
  let ew = 0, er = 0, hard = false;
  for (const o of chosen) {
    ew = Math.max(ew, o.ecutwfc || 0);
    er = Math.max(er, o.ecutrho || 0);
    hard ||= ["US", "PAW"].includes((o.pseudo_type || "").toUpperCase());
  }
  if (!ew) return null;
  // whole Rydbergs, rounded up: ecutwfc first, then ecutrho from it (the ratio still holds: 46.65 / 373.2 -> 47 / 376)
  const wfc = Math.ceil(ew - 1e-9);
  return { ecutwfc: wfc, ecutrho: Math.max(Math.ceil(er - 1e-9), (hard ? 8 : 4) * wfc) };
}

/** Number of k-points on the recipe's band path (the counts of all nodes but the last, plus one). */
export function pathPoints(r: Recipe): number {
  const pts: any[] = r?.bands?.path?.points ?? [];
  if (!pts.length) return 0;
  const n = (p: any) => Number(Array.isArray(p) ? p[3] : p?.n ?? 1) || 0;
  return pts.slice(0, -1).reduce((s, p) => s + n(p), 0) + 1;
}

export function pathLabels(r: Recipe): string {
  const pts: any[] = r?.bands?.path?.points ?? [];
  return pts.map((p) => (Array.isArray(p) ? p[4] : p?.label) || "·").map((l) => (l === "G" ? "Γ" : l)).join(" – ");
}

/** Issues by level, errors first. */
export function groupIssues(issues: QeIssue[]): Record<QeIssue["level"], QeIssue[]> {
  const out: Record<QeIssue["level"], QeIssue[]> = { error: [], warning: [], note: [] };
  for (const i of issues) (out[i.level] ?? out.note).push(i);
  return out;
}

// skfitter_qe's run-time model (calibrated in the QE-5 pilot): seconds = TIME_BASE_S + serial_seconds / nprocs^EXP.
// Keep these two in step with TIME_BASE_S and TIME_PARALLEL_EXP in skfitter/skfitter_qe.py.
export const QE_TIME_BASE_S = 5;
export const QE_TIME_PARALLEL_EXP = 0.5;

/** Seconds of the whole run (SCF, bands, projections) on `nprocs` MPI processes; null without an estimate. */
export function roughSeconds(e: QeEstimate | null, nprocs: number): number | null {
  if (e?.serial_seconds == null) return null;
  return QE_TIME_BASE_S + e.serial_seconds / Math.pow(Math.max(1, nprocs), QE_TIME_PARALLEL_EXP);
}

/** "about 2 min" etc. from the run-time model; only a rough guide (within a factor of about 1.5 in the pilot). */
export function roughTime(e: QeEstimate | null, nprocs: number): string | null {
  const seconds = roughSeconds(e, nprocs);
  if (seconds == null) return null;
  if (seconds < 90) return `about ${Math.round(seconds / 10) * 10} s`;
  if (seconds < 5400) return `about ${Math.round(seconds / 60)} min`;
  return `about ${(seconds / 3600).toFixed(1)} h`;
}

export function fmtEstimate(e: QeEstimate): string {
  const parts = [`${e.atoms} atom${e.atoms === 1 ? "" : "s"}`];
  if (e.plane_waves) parts.push(`~${e.plane_waves.toLocaleString()} plane waves`);
  if (e.kpoints_scf_irr && e.kpoints_scf) parts.push(`${e.kpoints_scf_irr} SCF k-points (${e.kpoints_scf} before symmetry)`);
  else if (e.kpoints_scf) parts.push(`${e.kpoints_scf} SCF k-points (before symmetry)`);
  parts.push(`${e.kpoints_bands} on the path`);
  if (e.bands) parts.push(`${e.bands} ${e.spinor === 2 ? "spinor " : ""}bands`);
  if (e.memory_mb) parts.push(`~${Math.round(e.memory_mb)} MB`);
  return parts.join(" · ");
}

// == the atoms of a recipe (the Structure tab's table) ==================================================================
export interface AtomRow { label: string; x: number; y: number; z: number }

/** The recipe's atoms as rows ([species, x, y, z] or {species, position}). */
export function atomRows(r: Recipe): AtomRow[] {
  return (r.structure?.atoms ?? []).map((a: any) => {
    const [label, x, y, z] = Array.isArray(a) ? a : [a.species, ...(a.position ?? [])];
    return { label: String(label), x: Number(x), y: Number(y), z: Number(z) };
  });
}

/** The recipe with these atoms. A new element gets a species of its own (its pseudopotential is then chosen in the
 * table); a species left without atoms is removed. */
export function withAtoms(r: Recipe, rows: AtomRow[]): Recipe {
  const species: Record<string, any> = { ...(r.species ?? {}) };
  for (const row of rows) if (!(row.label in species)) species[row.label] = { pseudo: null };
  const used = new Set(rows.map((row) => row.label));
  for (const label of Object.keys(species)) if (!used.has(label)) delete species[label];
  return { ...r, species, structure: { ...(r.structure ?? {}), atoms: rows.map((row) => [row.label, row.x, row.y, row.z]) } };
}
// == physics tools (G-3): moments, magnetic orders, supercell, DFT+U, the band path ===================================
export interface SiteMoment { m: number; theta: number | null; phi: number | null }
export interface MagneticOrder { name: string; kind: "FM" | "AFM" | "FiM"; signs: number[]; moments: number[]; spacegroup: string }
export interface HubbardRow { species: string; element: string; manifold: string; u: number | null; suggested: number | null; source: string }
export interface KpointChoices {
  points: { label: string; k: number[]; in_plane: boolean }[];
  standard: any[]; reciprocal: number[][]; dimensionality: string;
}
export const HUBBARD_PROJECTORS = ["ortho-atomic", "atomic", "norm-atomic"];

export const editMoments = (recipe: Recipe, moments: (number | SiteMoment | null)[]) =>
  post<{ recipe: Recipe; notes: string[]; moments: SiteMoment[] }>("/dft/edit/moments", { recipe, moments });
export const editOrders = (recipe: Recipe, elements: string[], magnitude: number) =>
  post<{ sites: number[]; orders: MagneticOrder[]; notes: string[] }>("/dft/edit/orders", { recipe, elements, magnitude });
export const editSupercell = (recipe: Recipe, matrix: number[] | number[][]) =>
  post<{ recipe: Recipe; notes: string[] }>("/dft/edit/supercell", { recipe, matrix });
export const editHubbard = (recipe: Recipe) => post<{ species: HubbardRow[] }>("/dft/edit/hubbard", { recipe });
export const editKpoints = (recipe: Recipe) => post<KpointChoices>("/dft/edit/kpoints", { recipe });

/** The starting magnetisation of each atom (from its species). */
export function siteMoments(r: Recipe): SiteMoment[] {
  const sp = r.species ?? {};
  const get = (label: string) => (Array.isArray(sp) ? sp.find((s: any) => s?.label === label) : sp[label]) ?? {};
  return atomRows(r).map((a) => {
    const s = get(a.label);
    return { m: Number(s.starting_magnetization ?? 0) || 0, theta: s.angle1 ?? null, phi: s.angle2 ?? null };
  });
}

/** The recipe with these DFT+U values (species-manifold -> U; empty or 0 leaves a species out). */
export function withHubbard(r: Recipe, projector: string, u: Record<string, number | null>): Recipe {
  const kept = Object.fromEntries(Object.entries(u).filter(([, v]) => v != null && v > 0)) as Record<string, number>;
  const out = { ...r };
  if (Object.keys(kept).length) out.hubbard = { projector, u: kept };
  else delete out.hubbard;
  return out;
}

export interface PathRow { label: string; k: number[]; n: number }
/** The band path's nodes (crystal_b); [] for another unit. */
export function pathRows(r: Recipe): PathRow[] {
  const p = r?.bands?.path;
  if (!p || (p.units ?? "crystal_b") !== "crystal_b") return [];
  return (p.points ?? []).map((x: any) => Array.isArray(x)
    ? { k: [Number(x[0]), Number(x[1]), Number(x[2])], n: Number(x[3] ?? 1), label: String(x[4] ?? "") }
    : { k: (x.k ?? [0, 0, 0]).map(Number), n: Number(x.n ?? 1), label: String(x.label ?? "") });
}
export const withPath = (r: Recipe, rows: PathRow[]): Recipe => ({
  ...r, bands: { ...(r.bands ?? {}), path: { units: "crystal_b", points: rows.map((x) => [...x.k, x.n, x.label]) } },
});

/** Shares ``total`` k-points over the segments by their length (a segment ending a branch - n = 1 - stays a jump). */
export function spreadPoints(rows: PathRow[], reciprocal: number[][], total: number): PathRow[] {
  const cart = (k: number[]) => [0, 1, 2].map((j) => k[0] * reciprocal[0][j] + k[1] * reciprocal[1][j] + k[2] * reciprocal[2][j]);
  const len = rows.map((r, i) => (i < rows.length - 1 && r.n !== 1
    ? Math.hypot(...cart(rows[i + 1].k).map((v, j) => v - cart(r.k)[j])) : 0));
  const sum = len.reduce((a, b) => a + b, 0) || 1;
  return rows.map((r, i) => (i === rows.length - 1 ? { ...r, n: 1 } : r.n === 1 ? r
    : { ...r, n: Math.max(2, Math.round((total * len[i]) / sum)) }));
}

// == defects (D-1) =======================================================================================================
export interface SiteClass {
  id: string; species: string; element: string; wyckoff: string; count: number; atoms: number[]; representative: number;
  frac: number[]; neighbours: { distance_A: number | null; species: Record<string, number> };
}
export interface Interstitial {
  id: string; frac: number[]; radius_A: number; count: number; neighbours: Record<string, number>; kind: string; saddle: boolean;
}
export interface SupercellOption {
  matrix: number[][]; cells: number; atoms: number; image_distance_A: number; lengths_A: number[]; diagonal: boolean;
  label: string; recommended: boolean; over_limit: boolean;
}
export interface DefectSites {
  sites: { spacegroup: string; number: number; classes: SiteClass[]; atoms: number; dimensionality: string };
  interstitials: Interstitial[];
  supercells: { recommended: SupercellOption; options: SupercellOption[]; host_atoms: number; host_image_distance_A: number;
    dimensionality: string; min_distance_A: number; max_atoms: number };
}
export type DefectKind = "vacancy" | "substitution" | "antisite" | "interstitial";
export interface DefectOp { kind: DefectKind; site?: number; atom?: number; element?: string; position?: number[] }
export interface DefectInfo {
  label: string; host: string; host_atoms: number; supercell: number[][]; cells: number; image_distance_A: number;
  operations: (DefectOp & { label: string; host_element: string | null; host_site?: number })[];
  center: number[] | null; atoms: number[]; vacancies: number[][]; atoms_total: number;
  shells?: number[][]; reference?: boolean; host_recipe?: Recipe;
  spin?: { on: "shell" | "defect"; start: number; moment: number | null; atoms: number[]; species: Record<string, string> };
  distortion?: { amplitude_A: number; seed: number; atoms: number[] };
  charge?: number;
}
/** Spins on a defect (D-2): collinear, a starting moment on its atoms and / or nearest neighbours; moment: the total fixed. */
export interface SpinSettings { on?: "shell" | "defect"; start?: number; moment?: number }
/** Symmetry breaking (D-2, with the relaxation): random displacements of the defect and its nearest neighbours. */
export interface DistortSettings { amplitude?: number; seed?: number }
export interface RelaxSettings { forc_conv_thr?: number; etot_conv_thr?: number; nstep?: number; fixed?: number[]; free_radius?: number; center?: number[] }

export const defectSites = (recipe: Recipe, minDistance: number) =>
  post<DefectSites>("/dft/defects/sites", { recipe, min_distance: minDistance });
export const defectBuild = (recipe: Recipe, matrix: number[][], operations: DefectOp[], relax: RelaxSettings | null, projectId?: string,
  more?: { spin?: SpinSettings | null; distort?: DistortSettings | null; charge?: number }) =>
  post<{ recipe: Recipe; notes: string[]; defect: DefectInfo }>("/dft/defects/build",
    { recipe, matrix, operations, relax, ...(projectId ? { project_id: projectId } : {}),
      ...(more?.spin ? { spin: more.spin } : {}), ...(more?.distort ? { distort: more.distort } : {}),
      ...(more?.charge ? { charge: more.charge } : {}) });
/** The host reference of a defect supercell (D-2): the perfect supercell with the defect run's settings. */
export const defectReference = (defectRecipe: Recipe, projectId?: string) =>
  post<{ recipe: Recipe; notes: string[]; defect: DefectInfo }>("/dft/defects/reference",
    { recipe: defectRecipe, ...(projectId ? { project_id: projectId } : {}) });

// == defect levels (D-2) =================================================================================================
export interface DefectLevel {
  band: number; channel: "all" | "up" | "down"; energy_eV: number; mean_eV: number; min_rel_eV: number; max_rel_eV: number;
  width_eV: number; occupation: number | null; electrons: number | null; localisation: number; localisation_2: number;
  participation: number; projected: number; where: "gap" | "valence band" | "conduction band"; to_cbm_eV: number;
  character: { element: string; l: string; share: number }[]; atoms: { atom: number; element: string; share: number }[];
}
export interface DefectStateRow {
  band: number; energy_eV: number; min_rel_eV: number; max_rel_eV: number; occupation: number | null; localisation: number | null;
  localised: boolean;
}
export interface DefectLevels {
  label: string | null; edges_from: string; shift_eV: number; vbm_eV: number; cbm_eV: number; gap_eV: number;
  fermi: { fermi?: number; fermi_up?: number; fermi_down?: number }; levels: DefectLevel[]; in_gap: number;
  states: Record<string, DefectStateRow[]>; spin: boolean;
  moments: { total?: number; absolute?: number; region?: number | null; region_2?: number | null;
    region_spheres?: number | null; per_atom_sum?: number; method?: "lowdin" | "band path" | "spheres";
    sites?: { atom: number; element: string; moment: number; sphere?: number; where: string }[] };
  region: { defect: number[]; shell1: number[]; shell2: number[] } | null; warnings: string[]; notes: string[];
  host_job: string | null; host_candidates: { job_id: string; name: string | null; created_at: string | null }[];
  alignment?: { method: string; per_element_eV: Record<string, number>; edge_check_eV: { vbm_eV?: number; cbm_eV?: number } };
}
// == formation energies and charge states (D-3) ==========================================================================
export const defectCharge = (recipe: Recipe, charge: number, projectId?: string) =>
  post<{ recipe: Recipe; notes: string[]; defect: DefectInfo }>("/dft/defects/charge",
    { recipe, charge, ...(projectId ? { project_id: projectId } : {}) });
export const defectElement = (recipe: Recipe, element: string, projectId?: string) =>
  post<{ recipe: Recipe; notes: string[] }>("/dft/defects/element", { recipe, element, ...(projectId ? { project_id: projectId } : {}) });
export interface FormationRun { job_id: string; name: string | null; created_at: string | null; atoms: number; q?: number }
export interface FormationSetup {
  label: string; host: string; supercell: number[][]; charges: FormationRun[]; hosts: FormationRun[];
  elements: Record<string, FormationRun[]>; needed: string[]; host_composition: Record<string, number>; impurities: string[];
  dimensionality: string;
}
export interface FormationCorrection {
  e_pc_eV: number; alignment_V: number; e_corr_eV: number; sampled: number; alignment_spread_V?: number; sample_radius_A?: number;
  profile?: { distance_A: number; dft_V: number; model_V: number; sampled: boolean }[];
}
export interface FormationCharge {
  q: number; label: string | null; job_id: string; name: string | null; added: Record<string, number>; e_defect_eV: number;
  e_host_eV: number; chemical_eV: number; q_vbm_eV: number; correction: FormationCorrection; formation_at_vbm_eV: number;
  formation_at_cbm_eV: number;
}
export interface FormationResult {
  label: string; charges: FormationCharge[]; segments: { q: number; from_eV: number; to_eV: number }[];
  transition_levels: { from_q: number; to_q: number; energy_eV: number; below_cbm_eV: number; label: string }[];
  all_pairs: { from_q: number; to_q: number; energy_eV: number; in_gap: boolean }[]; gap_eV: number; vbm_eV: number;
  mu: Record<string, number>; epsilon: number | null; dimensionality: string; warnings: string[]; host_job: string;
  chemical_potentials: Record<string, { mu: number; delta: number | null; ref: number | null; from: string }>;
  formation_enthalpy_eV: number | null; references: { element: string; job_id: string; name: string; energy_per_atom_eV: number }[];
  host_cell: Record<string, number>; rich: string | null;
}
export interface FormationBody {
  job_ids: string[]; host_job?: string | null; references: Record<string, string>; epsilon?: number | null; rich?: string | null;
  delta: Record<string, number>;
}
export const formationSetup = (jobId: string) => get<FormationSetup>(`/dft/runs/${jobId}/formation_setup`);
export const defectFormation = (body: FormationBody) => post<FormationResult>("/dft/defects/formation", body);
/** E_f of a charge state at a Fermi level (eV from the valence top). */
export const formationAt = (c: FormationCharge, ef: number) => c.formation_at_vbm_eV + c.q * ef;

export const qeDefectLevels = (jobId: string, host?: string | null) =>
  get<DefectLevels>(`/dft/runs/${jobId}/defect_levels${host ? `?host=${encodeURIComponent(host)}` : ""}`);

/** The label of a defect operation, as the builder names it: V_Si, C_B, As_Ga, Si_i. */
export function opLabel(op: DefectOp, hostElement?: string | null): string {
  if (op.kind === "vacancy") return `V_${hostElement ?? "?"}`;
  if (op.kind === "interstitial") return `${op.element ?? "?"}_i`;
  return `${op.element ?? "?"}_${hostElement ?? "?"}`;
}

/** The recipe with its relaxation set (null: none), keeping the other fields. */
export function withRelax(r: Recipe, relax: RelaxSettings | null): Recipe {
  const out = { ...r };
  if (relax == null) delete out.relax;
  else out.relax = Object.keys(relax).length ? relax : true;
  return out;
}
export const relaxOf = (r: Recipe): RelaxSettings | null =>
  r.relax == null || r.relax === false ? null : r.relax === true ? {} : (r.relax as RelaxSettings);

